import { PTag, PText } from "@porsche-design-system/components-react"
import type { FinancingView, InventoryInformationView, VisibilityView } from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import { FormattedMessage, defineMessages, useIntl } from "react-intl"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { commonMessages } from "src/common/messages/common.messages"
import { Flex, List, OnlineOrderingIcon, VisibilityStatus } from "src/components"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"
import { EditButton } from "../editButton/EditButton"

const messages = defineMessages({
	customerPrice: {
		defaultMessage: "Customer Price",
		id: "vehicleDetails.overview.summary.customerPrice",
	},
	financing: {
		defaultMessage: "Financing",
		id: "vehicleDetails.overview.summary.financing",
	},
	financingLinkText: {
		defaultMessage: "Select financing products",
		id: "vehicleDetails.overview.summary.financing.select",
	},
	listingTitle: {
		defaultMessage: "Listing",
		id: "vehicleDetails.overview.summary.listingTitle",
	},
	onlineOrderingLabel: {
		defaultMessage: "Online Orderable",
		id: "vehicleDetails.overview.summary.onlineOrdering.label",
	},
	visibility: {
		defaultMessage: "Visibility",
		id: "vehicleDetails.overview.summary.visibility",
	},
})

interface ListingListProps {
	financingView: FinancingView | undefined
	inventoryInformationView: InventoryInformationView | undefined | null
	isFinancingEnabled: boolean
	isOnlineOrderingEnabled: boolean
	onEditClick: (stage: VehicleDetailsStage) => void
	porscheApproved: string
}

export const ListingList = ({
	financingView,
	inventoryInformationView,
	isFinancingEnabled,
	isOnlineOrderingEnabled,
	onEditClick,
	porscheApproved,
}: ListingListProps) => {
	const { formatMessage } = useIntl()
	const [visibilityView, setVisibilityView] = useState<VisibilityView>()
	const { subscribe } = useVehicleDetailsContext()

	useEffect(() => {
		subscribe("visibilityChanged", setVisibilityView)
	}, [subscribe])
	return (
		<List header={formatMessage(messages.listingTitle)}>
			{inventoryInformationView !== null && (
				<List.Item
					label={formatMessage(messages.customerPrice)}
					value={
						inventoryInformationView && (
							<Flex $alignItems="center" $gap="xs">
								<PText weight="semibold" title={inventoryInformationView.pricing.customerPrice.value}>
									{inventoryInformationView.pricing.customerPrice.value ?? emptyPlaceholder}
								</PText>
								{inventoryInformationView.pricing.customerPrice.vatDeductible && (
									<PTag compact>
										<FormattedMessage {...commonMessages.vat} />
									</PTag>
								)}
								{inventoryInformationView.pricing.customerPrice.lastPriceChange && (
									<List.ItemValue
										value={`(${inventoryInformationView.pricing.customerPrice.lastPriceChange})`}
									/>
								)}
								{inventoryInformationView.pricing.pricingEdit && (
									<EditButton
										onClick={() => onEditClick("pricing")}
										actionItem={inventoryInformationView.pricing.pricingEdit}
									/>
								)}
							</Flex>
						)
					}
					valueSingleLine
				/>
			)}
			{isFinancingEnabled && financingView?.editAction && (
				<List.Item
					label={formatMessage(messages.financing)}
					value={
						<Flex $gap="xs">
							<List.ItemValue value={formatMessage(messages.financingLinkText)} />
							<EditButton
								onClick={() => onEditClick("financing")}
								actionItem={financingView.editAction}
							/>
						</Flex>
					}
					valueSingleLine
				/>
			)}
			<List.Item label={formatMessage(commonMessages.porscheApproved)} value={porscheApproved} valueSingleLine />
			{visibilityView && (
				<>
					<List.Item
						label={formatMessage(messages.visibility)}
						value={
							<Flex $gap="xs">
								<VisibilityStatus weight="semibold" view={visibilityView.visibility} />
								{visibilityView.editAction && (
									<EditButton
										onClick={() => onEditClick("visibility")}
										actionItem={visibilityView.editAction}
									/>
								)}
							</Flex>
						}
						valueSingleLine
					/>

					{isOnlineOrderingEnabled && (
						<List.Item
							label={formatMessage(messages.onlineOrderingLabel)}
							value={<OnlineOrderingIcon weight="semibold" view={visibilityView?.onlineOrdering} />}
							valueSingleLine
						/>
					)}
				</>
			)}
		</List>
	)
}
